import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, Signal, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	ButtonIconComponent,
	EmptyStateComponent,
	ItemComponent,
	ItemFilterComponent,
	ItemGroupComponent,
	IToolbar,
	LinkComponent,
	ListDetailComponent,
	ListLayoutComponent,
	PaginationComponent,
	TagComponent,
	TitleGroupComponent,
	ToolbarComponent,
} from '@tecalisdev/infinity';
import { Observable } from 'rxjs';
import { Permission } from '../../../../../core/models/role-permissions.models';
import { RolePermissionsService } from '../../../../../core/services/role-permissions.service';
import { parseActivityDeepLinkUuid } from '../../../../../core/utils/settings-activity-deep-link.util';
import { formatDate } from '../../../../../core/utils/utils';
import { environment } from '../../../../../../environments/environment';
import { buildCurlPostJson, CURL_BEARER_PLACEHOLDER } from '../../../../../panel/utils/build-curl.util';
import { ModalCurlRequestComponent } from '../../../../../shared/components/modal-curl-request/modal-curl-request.component';
import { PaginatedListController } from '../../../../../shared/list/paginated-list.controller';
import { MenuItemAction } from '../../../../../shared/models/master.models';
import { MasterService } from '../../../../../shared/services/master.service';
import { NavigationService } from '../../../../../shared/services/navigation.service';
import { ToolbarHelperService } from '../../../../../shared/services/toolbar-helper.service';
import {
	IThemeMode,
	ModalDesignThemeComponent,
} from '../../components/modal-design-theme/modal-design-theme.component';
import { IDesignListBody, IDesignListResponse } from '../../models/design.models';
import { DesignHelperService } from '../../services/design-helper.service';
import { DesignService } from '../../services/design.service';
import { DESIGN_THEME_FROM_LIST_STATE_KEY } from '../design-form/design-form.component';

@Component({
	selector: 'app-design-list',
	imports: [
		ListLayoutComponent,
		TitleGroupComponent,
		TranslatePipe,
		ButtonComponent,
		ButtonIconComponent,
		AlertComponent,
		ToolbarComponent,
		ItemComponent,
		ItemFilterComponent,
		ItemGroupComponent,
		ListDetailComponent,
		TagComponent,
		LinkComponent,
		PaginationComponent,
		EmptyStateComponent,
		ModalCurlRequestComponent,
		ModalDesignThemeComponent,
	],
	templateUrl: './design-list.component.html',
	styleUrl: './design-list.component.scss',
})
export class DesignListComponent extends PaginatedListController implements OnInit {
	private toolbarHelperService = inject(ToolbarHelperService);
	private designHelper = inject(DesignHelperService);
	private designService = inject(DesignService);
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private location = inject(Location);
	private trans = inject(TranslateService);
	private navigationService = inject(NavigationService);
	private masterService = inject(MasterService);
	public rolePermissions = inject(RolePermissionsService);

	protected readonly Permission = Permission;

	// LIST
	public toolbar: Signal<IToolbar>;
	public designList: IDesignListBody[];
	public override config = this.designHelper.config;
	public readonly listSkeletonItems = [1, 2, 3, 4, 5];

	// GENERAL
	public readonly curlModalOpen = signal(false);
	public readonly curlModalTitleKey = signal('PANEL.CURL_MODAL.TITLE');
	public readonly curlModalText = signal('');
	public readonly showCreateThemeModal = signal(false);

	ngOnInit(): void {
		const uuid = parseActivityDeepLinkUuid(this.route);
		if (uuid) {
			void this.router.navigate(['/settings', 'design', 'detail', uuid], { replaceUrl: true });
			return;
		}
		this.toolbarHelperService.initializeListToolbar(this, () => this.designHelper.toolbar);
	}

	protected createListRequest(): Observable<IDesignListResponse> {
		this.getPayload();
		return this.designService.list(this.filters);
	}

	protected applyListResponse(response: IDesignListResponse): void {
		this.designList = response.data.map((design) => this.formatDesignListItem(design));
	}

	protected override onListError(_error: HttpErrorResponse): void {
		this.alertError = this.trans.instant('ERRORS.GENERIC_DESCRIPTION');
	}

	public getCompanyName(design: IDesignListBody): string {
		if (!design?.company_uuid) return '';
		return this.masterService.getNameCompany(design.company_uuid) || '';
	}

	private formatDesignListItem(design: IDesignListBody): IDesignListBody {
		if (design.created_at) {
			design.created_at = formatDate(design.created_at);
		}
		return design;
	}

	private getPayload(): void {
		this.formatFilters();
		const { sort_by: _ignoredSortBy, sort_order: _ignoredSortOrder, ...rest } = this.filters ?? {};
		this.filters = this.withPagination(rest as Record<string, unknown>, {
			sort_by: 'created_at',
			sort_order: 'desc',
		});
		this.toolbarHelperService.cleanSavedToolbar(this.constructor.name);
	}

	/**
	 * Formatea los filtros eliminando los vacíos y ajustando la propiedad 'active' si está presente.
	 * Si la propiedad 'active' existe y es verdadera, asigna su valor; de lo contrario, la elimina.
	 */
	private formatFilters(): void {
		if (!this.filters) return;

		this.clearEmptyFilters();
		if (Object.hasOwn(this.filters, 'active')) {
			if (this.filters['active'] !== null && this.filters['active'] !== undefined) {
				this.filters['active'] = this.filters['active']?.value ?? this.filters['active'];
			} else delete this.filters['active'];
		}
	}

	/**
	 * Limpia los filtros vacíos.
	 * Elimina las claves del objeto filters que tienen valores vacíos, nulos o indefinidos.
	 */
	private clearEmptyFilters(): void {
		if (!this.filters) return;

		Object.keys(this.filters).forEach((key) => {
			if (this.filters[key] === '' || this.filters[key] === null || this.filters[key] === undefined) {
				delete this.filters[key];
			}
		});
	}

	public getDesignListCurl(): string {
		if (!this.filters) return '';
		return buildCurlPostJson(environment.apiUrl, '/design-configs', this.filters as object);
	}

	public getDesignDetailCurl(uuid: string | undefined): string {
		if (!uuid) return '';
		const base = environment.apiUrl.replace(/\/$/, '');
		const url = `${base}/design-config/${uuid}`;
		return [`curl -X GET "${url}" \\`, `  -H "Authorization: Bearer ${CURL_BEARER_PLACEHOLDER}"`].join('\n');
	}

	public openListCurlModal(): void {
		this.curlModalTitleKey.set('PANEL.CURL_MODAL.TITLE_DESIGN_LIST');
		this.curlModalText.set(this.getDesignListCurl());
		this.curlModalOpen.set(true);
	}

	public openDetailCurlModal(uuid: string | undefined): void {
		this.curlModalTitleKey.set('PANEL.CURL_MODAL.TITLE_DESIGN_DETAIL');
		this.curlModalText.set(this.getDesignDetailCurl(uuid));
		this.curlModalOpen.set(true);
	}

	public onToolbarAction(action: string | { id?: string }): void {
		const id = typeof action === 'string' ? action : action?.id;
		if (id === MenuItemAction.SHOW_CURL) {
			this.openListCurlModal();
		}
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	/** Abre el modal de tema sobre el listado (NIF-750 / comentario Carmen). */
	public goToCreate(): void {
		this.showCreateThemeModal.set(true);
	}

	public closeCreateThemeModal(event?: IThemeMode | null): void {
		this.showCreateThemeModal.set(false);
		if (event && event.title?.length > 0) {
			void this.router.navigate(['/settings/design/create'], {
				state: { [DESIGN_THEME_FROM_LIST_STATE_KEY]: event },
			});
		}
	}

	public goToDetail(designId: string, event?: MouseEvent | null): void {
		const basePath = '/' + (this.location?.path().split('/').filter(Boolean)[0] ?? 'settings');
		this.toolbarHelperService.saveToolbarPagination(
			this.filters ?? {},
			this.toolbar,
			this.config.pagination,
			basePath,
			null,
			this.constructor.name,
		);
		this.navigationService.navigateTo({ routeSegments: ['settings', 'design', 'detail', designId] }, event);
	}
}
